/* ===========================================================================
 * The STRIP that appears above a list to say something about it — and the one
 * inset it is drawn with.
 *
 * WHAT THIS OBJECT IS. Not a card and not a toolbar: a full-width strip that
 * turns up between the controls and the rows, states one fact about the rows
 * ("12 selected", "All 50 on this page are selected", "That save didn't go
 * through"), and usually offers the one thing to do about it. The app had four
 * of them and they agreed on nothing:
 *
 *   employees · bulk selection      min-h-[3rem] rounded-card px-3.5 py-2.5
 *   employees · select-all-matching px-tile py-2, no radius, no min-height
 *   schedule  · cell selection      <Card> + p-3
 *   schedule  · failed write        <Card> + p-3
 *
 * Three insets, two structures, one arbitrary height — for four instances of
 * one object, two of which sit on the same screen. That is what "the padding
 * looks different from screen to screen" is made of.
 *
 * EVERY VALUE HERE IS A TOKEN. `px-cell-x py-cell-y` is the app's table inset
 * (16/12), which is the right one for this strip and not a coincidence: the bar
 * sits directly above the rows and its text lines up with the column beneath
 * it. `min-h-touch` is WCAG 2.5.5's target floor, which the bar needs anyway
 * because it carries buttons — and it replaces a hand-written `3rem` that was
 * 4px above the floor for no stated reason. Nothing in this file is a number.
 *
 * `rounded-panel`, AND THE TWO CALL SITES DISAGREED ABOUT THIS TOO. Radius is
 * by ROLE, not by size (rules §1): `--radius-card` is 12px and means "inner
 * card, nested panel"; `--radius-panel` is 16px and means "card, tile, table
 * shell". This strip is a page-level sibling of the table it sits above, not a
 * panel nested inside one — which is the argument the schedule's own bar spelled
 * out before it was folded in here, and it is the one the roles support. The
 * employees bar was `rounded-card` on the reasoning that 20px of inline air made
 * it read as a card in its own right; that was a padding problem, and the inset
 * above is the fix for it.
 *
 * MIN-HEIGHT, NEVER A HEIGHT. An Arabic count that wraps grows the bar instead
 * of clipping it — the same anatomy rule `Button` and `ControlBar` follow.
 *
 * WHY A CLASS AND NOT A COMPONENT. The four call sites differ in what they
 * wrap: one is a sticky animated container, one sits inside a `<Card>` that
 * owns its own border, two are plain `<div>`s inside a table card. A component
 * would have to take all of that as props and would end up a `<div>` with a
 * className anyway. The SKIN is what has to agree, so the skin is what is
 * shared — the same reason `SHIFT_CHIP_CLASS` is a constant and the same
 * technique `controlBarSearchClass` uses.
 * ======================================================================== */

/**
 * The strip's own geometry: layout, inset, radius and the target floor.
 * Tone is deliberately NOT here — a selection bar is brand-tinted, a failed
 * write is danger-tinted, and an escalation notice is info-tinted. Add the
 * triple (`border-*` / `bg-*` / text ink) at the call site.
 *
 *     <div className={`${noticeBarClass} border-primary bg-primary-tint`}>
 */
export const noticeBarClass =
  'flex min-h-touch flex-wrap items-center justify-between gap-element ' +
  'rounded-panel border px-cell-x py-cell-y';

/**
 * The same strip when it spans the full width of the card it sits in — the
 * select-all-matching notices, which are flush against the table's top edge and
 * take a bottom hairline instead of a full border and a radius.
 */
export const noticeBarFlushClass =
  'flex min-h-touch flex-wrap items-center justify-center gap-element ' +
  'border-b px-cell-x py-cell-y text-center';

/* ---------------------------------------------------------------------------
 * THE INLINE NOTICE — the same family, one size down.
 *
 * A tinted sentence with an icon, sitting inside a form or a card: "That save
 * didn't go through", "Check your inbox", the partial-result caveat, the
 * letters queue's failed decision. Where `noticeBarClass` is a page-level strip
 * above a list, this is a block inside content — which is why it takes
 * `rounded-card`, the nested role, where the strip takes `rounded-panel`.
 *
 * It shipped TEN ways before it had a name: `px-3 py-2.5` twelve times, `p-3`
 * eight, `px-3 py-2` four, plus `p-2`, `p-1.5` and four card-sized spellings.
 * Same object, same tint triple, ten insets. `--pad-notice-*` is the one it has
 * now; see the note on the token for why 12/10 rather than a card's 20.
 *
 * `items-start`, not `items-center`: these carry an icon beside text that
 * wraps to two or three lines in Arabic, and centring pins the icon to the
 * middle of the block instead of to the first line.
 * ------------------------------------------------------------------------ */

/**
 * Tone is the call site's, as with the strip above:
 *
 *     <p className={`${noticeInlineClass} border-danger-border bg-danger-surface text-danger-on-surface`}>
 */
export const noticeInlineClass =
  'flex items-start gap-element rounded-card border px-notice-x py-notice-y text-copy';
